import { useMemo, useState } from 'react';
import type { PointsScope } from './definition';
import { IssueList, Labelled, NumberInput, controlClass, smallButtonClass } from './controls';
import { eventListNotice } from './eventOptions';
import { issuesFor, type DefinitionValidation } from './validate';

/**
 * §3.2's rules, as controls.
 *
 * An event is scored when **all** of these hold: it belongs to the room, its date falls in the
 * range, it carries at least one required tag (when any are required), it carries none of the
 * excluded ones, its field is big enough, and it is not on the never-list. And then one override
 * beats the lot: an event on the always-list is in, whatever else says otherwise.
 *
 * That ordering is stated on the screen rather than left to be discovered, because operators hit
 * it — "I excluded the seniors tag but this event is still scoring" is the always-list winning,
 * and there is nothing in the numbers to suggest it.
 *
 * One quantifier is worth not mis-reading: required tags here are **any one of them**, unlike the
 * tournaments list, where a tag filter requires every tag selected. The tags are chosen with the
 * same one-chip-per-tag control as everywhere else — required → excluded → neither — so the hint
 * under it is the only thing that can carry the difference, and it does.
 *
 * **Where this appears has changed (§3.2, §9.2, §9.8).** It used to be section 2 of the definition
 * pane, which said — wrongly — that a points system carries dates. It now lives in the *preview*
 * pane as a test harness. §9.9's leaderboard screen does **not** use it: it edits the same
 * `PointsScope` through a filter-then-tick list instead (`LeaderboardScopeFilters` in room-web),
 * because there the subject is a season an operator already knows, not a rule they are composing.
 * This shape — every field of §4.2, laid out one per row — is the right one for the designer,
 * where the rule *is* the subject and a test scope has no other source for its dates.
 */

export interface ScopeEventOption {
  id: string;
  /** What the operator calls it — the room's own reference and the name. */
  label: string;
  /** `yyyy-mm-dd`, for the secondary line. */
  date: string;
  tags: string[];
}

/** How many matches the override list shows before it asks for a narrower search. */
const MAX_LISTED = 40;

export function ScopeEditor({
  value,
  onChange,
  tags,
  events,
  eventsTruncated = false,
  eventsFailed = false,
  validation,
  readOnly = false,
}: {
  value: PointsScope;
  onChange: (next: PointsScope) => void;
  /** The room's tag vocabulary. */
  tags: readonly string[];
  /**
   * The room's events, for the two override lists — or null while they are being read or when the read failed
   * (known gap 112), which the lists say rather than reporting a room with none.
   */
  events: readonly ScopeEventOption[] | null;
  /** Whether a null `events` is a read that failed rather than one still on its way. */
  eventsFailed?: boolean;
  /**
   * Whether `events` is a capped slice of the room's schedule rather than all of it. Said out
   * loud when it is — a picker that quietly lists a subset is how an operator concludes an event
   * doesn't exist.
   */
  eventsTruncated?: boolean;
  validation: DefinitionValidation;
  readOnly?: boolean;
}) {
  const [showOverrides, setShowOverrides] = useState(
    () => value.includeEventIds.length > 0 || value.excludeEventIds.length > 0,
  );

  const set = (patch: Partial<PointsScope>) => onChange({ ...value, ...patch });

  return (
    <div className="space-y-4">
      {/* Native date boxes have a min-content width around 150px and cannot usefully shrink, so
          they get their own row rather than a flex slot that collapses the control and lands its
          label on top of the next one. */}
      {/*
        A CLEARED BOX IS AN OPEN BOUND, NOT A BLANK FIELD (§4.2).

        `<input type="date">` reports `''` when empty and `points-scope.schema.json` refuses that
        against its `yyyy-mm-dd` pattern, so blank is mapped to null on the way out and back on the
        way in. The hint says so: without it a cleared box reads as something left unfilled, and an
        operator puts a date in to make the form look finished — which narrows the scope.
      */}
      <div className="grid gap-3 sm:grid-cols-2">
        <Labelled label="First day" hint="Leave blank for no earliest date.">
          <input
            type="date"
            value={value.startDate ?? ''}
            disabled={readOnly}
            onChange={(e) => set({ startDate: e.target.value || null })}
            className={`${controlClass} w-full`}
          />
        </Labelled>
        <Labelled label="Last day" hint="Both days are included. Blank for no latest date.">
          <input
            type="date"
            value={value.endDate ?? ''}
            disabled={readOnly}
            onChange={(e) => set({ endDate: e.target.value || null })}
            className={`${controlClass} w-full`}
          />
        </Labelled>
      </div>

      <ScopeTagPicker
        tags={tags}
        includeTags={value.includeTags}
        excludeTags={value.excludeTags}
        onChange={(includeTags, excludeTags) => set({ includeTags, excludeTags })}
        readOnly={readOnly}
      />

      <Labelled
        label="Smallest field that counts"
        hint="Events with fewer entries than this are left out. 0 takes them all."
        className="max-w-[12rem]"
      >
        <NumberInput
          value={value.minFieldSize}
          onChange={(n) => set({ minFieldSize: n ?? 0 })}
          ariaLabel="Smallest field that counts"
          min={0}
          step={1}
          disabled={readOnly}
          className="w-full"
        />
      </Labelled>

      <IssueList issues={issuesFor(validation, 'scope')} />

      <div>
        <button
          type="button"
          onClick={() => setShowOverrides((open) => !open)}
          className={smallButtonClass}
          aria-expanded={showOverrides}
        >
          {showOverrides ? 'Hide' : 'Name'} individual events
          {countOverrides(value) > 0 ? ` (${countOverrides(value)})` : ''}
        </button>

        {showOverrides ? (
          <div className="mt-3 space-y-3 border-l-2 border-line pl-4">
            <p className="text-[11.5px] leading-relaxed text-ink-800/60">
              For the exceptions the rules above can’t express. <strong>Always scores</strong> wins
              over everything else — the dates, the tags, the field size — so an event listed there
              counts even if nothing else would let it. <strong>Never scores</strong> beats the tag
              rules but not that.
            </p>

            <EventPicker
              title="Always scores"
              selected={value.includeEventIds}
              onToggle={(id) => set({ includeEventIds: toggle(value.includeEventIds, id) })}
              events={events}
              truncated={eventsTruncated}
              failed={eventsFailed}
              readOnly={readOnly}
            />
            <EventPicker
              title="Never scores"
              selected={value.excludeEventIds}
              onToggle={(id) => set({ excludeEventIds: toggle(value.excludeEventIds, id) })}
              events={events}
              truncated={eventsTruncated}
              failed={eventsFailed}
              readOnly={readOnly}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
}

function countOverrides(scope: PointsScope): number {
  return scope.includeEventIds.length + scope.excludeEventIds.length;
}

function toggle(list: string[], item: string): string[] {
  return list.includes(item) ? list.filter((x) => x !== item) : [...list, item];
}

/** A tag's role in the scope: required, vetoed, or neither. */
type ScopeTagState = 'off' | 'include' | 'exclude';

const CHIP: Record<ScopeTagState, string> = {
  off: 'rounded-full border border-line px-2.5 py-1 text-xs text-ink-800/70 transition hover:border-felt-500 disabled:hover:border-line',
  include: 'rounded-full bg-felt-700 px-2.5 py-1 text-xs font-medium text-white',
  exclude:
    'rounded-full border border-ink-800/40 bg-ink-950/5 px-2.5 py-1 text-xs text-ink-800/60 line-through decoration-ink-800/50',
};

/** Spoken state, because a strike-through and a colour say nothing to a screen reader. */
const CHIP_LABEL: Record<ScopeTagState, (tag: string) => string> = {
  off: (tag) => `${tag}: not filtered. Activate to require it.`,
  include: (tag) => `${tag}: required. Activate to exclude it.`,
  exclude: (tag) => `${tag}: excluded. Activate to stop filtering on it.`,
};

/**
 * One chip per tag, cycling required → excluded → neither.
 *
 * The same control and the same gesture as everywhere else tags are filtered on — room-web's
 * `TagFilterPicker`, used by the tournament list and by §9.9's leaderboard scope. It was two lists
 * here, one to require and one to exclude, on the reasoning that the two quantifiers differ. They
 * do, but that is a fact about the *rule* rather than about the gesture: two rows listed the
 * vocabulary twice, and a tag's state then had to be read by finding which of the two lists it was
 * sitting in.
 *
 * <b>The quantifier is still §3.2's and still not the tournaments list's</b> — a required tag here
 * is *any one of them*, not all of them. Nothing in the chips can say that, which is why the hint
 * below states it outright whenever more than one is required, exactly as
 * `LeaderboardScopeFilters` does for the same scope on the same rule.
 *
 * Cycling through one function is also what keeps a tag out of both lists at once — the
 * combination that matches nothing by construction and that `PointsScopeValidator` refuses.
 */
function ScopeTagPicker({
  tags,
  includeTags,
  excludeTags,
  onChange,
  readOnly,
}: {
  tags: readonly string[];
  includeTags: string[];
  excludeTags: string[];
  onChange: (includeTags: string[], excludeTags: string[]) => void;
  readOnly: boolean;
}) {
  const stateOf = (tag: string): ScopeTagState =>
    includeTags.includes(tag) ? 'include' : excludeTags.includes(tag) ? 'exclude' : 'off';

  const cycle = (tag: string) => {
    const include = includeTags.filter((t) => t !== tag);
    const exclude = excludeTags.filter((t) => t !== tag);

    switch (stateOf(tag)) {
      case 'off':
        return onChange([...include, tag], exclude);
      case 'include':
        return onChange(include, [...exclude, tag]);
      default:
        return onChange(include, exclude);
    }
  };

  return (
    <div className="min-w-0">
      <div className="mb-1 text-xs text-ink-800/60">Filter by tag</div>
      {tags.length === 0 ? (
        <p className="text-[11.5px] text-ink-800/45">No tags have been used in this room yet.</p>
      ) : (
        <div role="group" aria-label="Filter by tag" className="flex flex-wrap gap-1.5">
          {tags.map((tag) => {
            const state = stateOf(tag);
            return (
              <button
                key={tag}
                type="button"
                disabled={readOnly}
                onClick={() => cycle(tag)}
                aria-label={CHIP_LABEL[state](tag)}
                className={CHIP[state]}
              >
                {state === 'exclude' ? '− ' : ''}
                {tag}
              </button>
            );
          })}
        </div>
      )}
      <p className="mt-1 text-[11px] leading-relaxed text-ink-800/45">
        {tagHint(includeTags, excludeTags)}
      </p>
    </div>
  );
}

/**
 * The line under the chips. Says which quantifier is in play rather than leaving it to be inferred
 * from the colours — an operator arriving from the tournaments list has been taught the other one.
 */
function tagHint(includeTags: string[], excludeTags: string[]): string {
  const gesture = 'Click once to require a tag, twice to exclude it.';

  if (includeTags.length > 1) {
    return `${gesture} An event needs at least one of the required tags — not all of them.`;
  }
  if (excludeTags.length > 0) {
    return `${gesture} Struck-through tags keep an event out whatever else it carries.`;
  }
  return `${gesture} With none selected, every event in the range counts.`;
}

function EventPicker({
  title,
  selected,
  onToggle,
  events,
  truncated,
  failed,
  readOnly,
}: {
  title: string;
  selected: string[];
  onToggle: (id: string) => void;
  events: readonly ScopeEventOption[] | null;
  truncated: boolean;
  failed: boolean;
  readOnly: boolean;
}) {
  const [query, setQuery] = useState('');

  const { listed, hidden } = useMemo(() => {
    const needle = query.trim().toLowerCase();
    const matches = (events ?? []).filter(
      (event) =>
        selected.includes(event.id) ||
        needle === '' ||
        event.label.toLowerCase().includes(needle) ||
        event.date.includes(needle),
    );
    // Chosen events stay listed whatever the search says, so unticking one never requires
    // reconstructing the search that found it.
    const chosenFirst = [
      ...matches.filter((e) => selected.includes(e.id)),
      ...matches.filter((e) => !selected.includes(e.id)),
    ];
    return {
      listed: chosenFirst.slice(0, MAX_LISTED),
      hidden: Math.max(chosenFirst.length - MAX_LISTED, 0),
    };
  }, [events, query, selected]);
  const notice = eventListNotice(events, failed);

  return (
    <div className="min-w-0">
      <div className="mb-1.5 flex flex-wrap items-center justify-between gap-2">
        <span className="text-xs text-ink-800/60">
          {title}
          {selected.length > 0 ? <span className="num text-ink-800/40"> · {selected.length}</span> : null}
        </span>
        <input
          type="search"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search events"
          aria-label={`Search events to add to “${title}”`}
          className={`${controlClass} w-40`}
        />
      </div>

      {notice !== null ? (
        <p className="text-[11.5px] text-ink-800/45">{notice}</p>
      ) : (
        <div className="max-h-44 overflow-y-auto border border-line bg-surface">
          {listed.map((event) => (
            <label
              key={event.id}
              className={`flex items-start gap-2 border-b border-line px-2.5 py-1.5 last:border-b-0 ${
                readOnly ? '' : 'cursor-pointer hover:bg-ground'
              }`}
            >
              <input
                type="checkbox"
                checked={selected.includes(event.id)}
                disabled={readOnly}
                onChange={() => onToggle(event.id)}
                className="mt-0.5 accent-[var(--color-felt-700)]"
              />
              <span className="min-w-0">
                <span className="block truncate text-[12px] text-ink-950">{event.label}</span>
                <span className="num block text-[10.5px] text-ink-800/45">{event.date}</span>
              </span>
            </label>
          ))}
          {listed.length === 0 ? (
            <p className="px-2.5 py-3 text-[11.5px] text-ink-800/45">Nothing matches that search.</p>
          ) : null}
        </div>
      )}

      {/* Never a silent cap: a list showing 40 of 300 reads exactly like a list showing all of them. */}
      {hidden > 0 ? (
        <p className="mt-1 text-[11px] text-ink-800/45">
          {hidden} more match. Narrow the search to reach them.
        </p>
      ) : null}
      {truncated ? (
        <p className="mt-1 text-[11px] text-warn">
          Not every event could be read, so this list is short of the room’s — reload to try again.
        </p>
      ) : null}
    </div>
  );
}
